import { ZodDefault, ZodNullable, ZodOptional, ZodTypeAny, z } from "zod";

// parses string JSONS and returns the string if it fails
export function safeParseJSON<T>(string: string): string | T {
	try {
		return JSON.parse(string);
	} catch {
		return string;
	}
}

function resolveCoreType(zodType: ZodTypeAny): ZodTypeAny {
	let currentType = zodType;

	// Unwrap recursively until the core type is found
	while (
		currentType instanceof ZodOptional ||
		currentType instanceof ZodNullable ||
		currentType instanceof ZodDefault ||
		(currentType?._def && currentType?._def?.innerType)
	) {
		if (
			currentType instanceof ZodOptional ||
			currentType instanceof ZodNullable ||
			currentType instanceof ZodDefault
		) {
			currentType = currentType?._def?.innerType;
		} else {
			currentType = currentType?._def?.innerType || currentType?._def; // Handles extra unwrapping for custom types.
		}
	}

	return currentType;
}

// converts a zod schema to a zod schema that accepts a URLSearchParams object
export function makeSearchParamsObjSchema<Schema extends z.ZodObject<z.ZodRawShape>>(schema: Schema) {
	return z
		.instanceof(URLSearchParams)
		.transform((params) => searchParamsToValuesWithSchema(params, schema))
		.pipe(schema);
}

// converts a URLSearchParams object to a record of values
export function searchParamsToValuesWithSchema<Schema extends z.ZodObject<z.ZodRawShape>>(
	searchParams: URLSearchParams,
	schema: Schema
): Record<string, any> {
	const shape = schema._def.shape();

	const result = Array.from(searchParams.keys()).reduce(
		(record, key) => {
			const schemaType = resolveCoreType(shape[key]); // Resolve to the core type

			let value: any = null;

			switch (schemaType?._def?.typeName) {
				case "ZodArray":
					value = searchParams.getAll(key).flatMap((value) => {
						if (value === "") {
							return [];
						}
						// Split comma-separated values if the schema expects an array
						const val = value.split(",");
						return val;
					});
					break;

				case "ZodBoolean":
					value = searchParams.get(key) === "true";
					break;

				case "ZodNumber":
					value = Number(searchParams.get(key));
					break;

				case "ZodDate": {
					const dateString = searchParams.get(key);
					value = dateString ? new Date(dateString) : null;
					break;
				}
				default:
					value = searchParams.get(key);
					break;
			}

			// Assign values based on whether the schema expects an array
			record[key] = value;

			return record;
		},
		{} as Record<string, any>
	);

	return result;
}

// coerces the sting array from URL Params to a true array
export function coerceToArray<Schema extends z.ZodArray<z.ZodTypeAny>>(schema: Schema) {
	return z.union([z.any().array(), z.any().transform((x) => [x])]).pipe(schema);
}
